import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Printer, Calendar, ArrowLeft, FileText, Loader2,
  Wrench, ShieldCheck, ClipboardList, Boxes, ChevronRight, Download, History,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Print Stack — preview + batch-print page for daily operational paperwork.
 *
 * Flow:
 *   1) Pick a doc type (Work Sheet / Materials / Contractor / Safety)
 *   2) Pick a period (day / week / fortnight / month)
 *   3) Review candidate jobs, tick/untick the ones to include
 *   4) Click "Generate Stack" → backend merges per-job PDFs → download
 *
 * Each stack is a single combined PDF so the office can print the whole
 * batch with one queue submission on Monday morning (etc.).
 */
const DOC_TYPES = [
  { id: 'work_sheet',        label: 'Work Sheets',         icon: FileText,      color: 'amber' },
  { id: 'materials_order',   label: 'Materials Orders',    icon: Boxes,         color: 'sky' },
  { id: 'contractor_report', label: 'Contractor Reports',  icon: Wrench,        color: 'emerald' },
  { id: 'safety_plan',       label: 'Safety Planning',     icon: ShieldCheck,   color: 'red' },
];

const PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'fortnight', label: 'Fortnight' },
  { id: 'month', label: 'Month' },
];

export default function PrintStackPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [docType, setDocType] = useState('work_sheet');
  const [period, setPeriod]   = useState('week');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState({}); // { appraisal_id: true }
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/print-stack/history?limit=20');
      setHistory(data?.entries || []);
    } catch {
      setHistory([]);
    }
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/print-stack/preview?doc_type=${docType}&period=${period}`);
      setPreview(data);
      // pre-select all candidates
      const all = {};
      (data?.candidates || []).forEach(c => { all[c._id] = true; });
      setSelected(all);
    } catch {
      setPreview(null); setSelected({});
    }
    setLoading(false);
  }, [api, docType, period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const toggleAll = (on) => {
    if (!preview?.candidates) return;
    const next = {};
    preview.candidates.forEach(c => { next[c._id] = on; });
    setSelected(next);
  };

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const generate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Pick at least one job to include in the stack.');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post('/print-stack/generate', {
        doc_type: docType,
        appraisal_ids: selectedIds,
      }, { responseType: 'blob' });
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const label = (DOC_TYPES.find(d => d.id === docType)?.label || 'Stack').replace(/\s+/g, '_');
      a.download = `RTR_Stack_${label}_${selectedIds.length}jobs_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const failed = res.headers?.['x-stack-failures'];
      const successes = res.headers?.['x-stack-successes'];
      if (successes === '0') {
        toast.error(`None of the ${selectedIds.length} selected jobs could be rendered. The downloaded PDF lists why — open it to see the failures.`, { duration: Infinity });
      } else if (failed) {
        toast.warning(`Stack ready — ${failed} job(s) failed to render. Open the PDF to see partial results.`);
      } else {
        toast.success(`Stack of ${selectedIds.length} ${DOC_TYPES.find(d => d.id === docType)?.label.toLowerCase()} ready to print.`);
        loadHistory();
      }
    } catch (err) {
      // Backend returns 500 with {detail, failures} when ALL per-job
      // renders fail. Surface a helpful toast instead of a silent
      // browser-console error.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const failures = err?.response?.data?.failures;
      if (status === 500 && failures) {
        toast.error(`Stack build failed — every selected job errored during render. ${failures.length} job(s) couldn't generate.`, { duration: Infinity });
      } else if (status === 400) {
        toast.error(typeof detail === 'string' ? detail : 'Bad stack request');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to build stack — try again or pick fewer jobs.');
      }
    }
    setGenerating(false);
  };

  const activeDoc = DOC_TYPES.find(d => d.id === docType) || DOC_TYPES[0];
  const ActiveIcon = activeDoc.icon;

  return (
    <div data-testid="print-stack-page" className="space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/pipeline')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#D35400] transition-colors font-medium">
          <ArrowLeft size={16} /> Back to Pipeline
        </button>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
        <header className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-orange-50">
            <Printer size={20} className="text-[#D35400]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Print Stack</h1>
            <p className="text-xs text-slate-500">Batch-print the week's operational paperwork in one click. Pick a doc type + time period, confirm the jobs, generate a combined PDF.</p>
          </div>
        </header>

        {/* Doc type pills */}
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map(dt => {
            const Icon = dt.icon;
            const active = dt.id === docType;
            return (
              <button
                key={dt.id}
                data-testid={`stack-doc-${dt.id}`}
                onClick={() => setDocType(dt.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md border transition-all ${
                  active
                    ? 'bg-[#D35400] text-white border-[#D35400]'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-orange-300 hover:bg-amber-50'
                }`}
              >
                <Icon size={12} /> {dt.label}
              </button>
            );
          })}
        </div>

        {/* Period pills */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Period:</span>
          {PERIODS.map(p => (
            <button
              key={p.id}
              data-testid={`stack-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border transition-all ${
                period === p.id
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              <Calendar size={10} /> {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate list */}
      <div className="bg-white border border-[#E2E2DF] rounded-md">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <ActiveIcon size={16} className="text-[#D35400]" />
            <div>
              <h2 className="text-sm font-bold text-slate-800">{activeDoc.label} — {preview?.from} → {preview?.to}</h2>
              <p className="text-[11px] text-slate-500">
                {loading
                  ? 'Loading candidates…'
                  : `${preview?.count || 0} candidate job${(preview?.count || 0) === 1 ? '' : 's'} match status: ${(preview?.statuses_used || []).join(', ')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="stack-select-all" onClick={() => toggleAll(true)} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-[#D35400]">Select all</button>
            <button data-testid="stack-select-none" onClick={() => toggleAll(false)} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-[#D35400]">None</button>
            <button
              data-testid="stack-generate-btn"
              onClick={generate}
              disabled={generating || selectedIds.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#D35400] text-white rounded-md hover:bg-[#A04000] disabled:opacity-40 transition-colors"
            >
              {generating ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              Generate Stack ({selectedIds.length})
            </button>
          </div>
        </header>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : (preview?.candidates || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">
              No jobs match {activeDoc.label.toLowerCase()} for this period yet.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Property</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Booked</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Contractor</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.candidates.map(c => (
                  <tr key={c._id} className="hover:bg-amber-50/30">
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`stack-row-${c._id}`}
                        checked={!!selected[c._id]}
                        onChange={e => setSelected(prev => ({ ...prev, [c._id]: e.target.checked }))}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono-num font-bold text-slate-800">#{c.appraisal_no || c._id.slice(-6)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800 truncate max-w-[180px]">{c.client_name}</td>
                    <td className="px-3 py-2 hidden md:table-cell text-slate-600 truncate max-w-[260px]">{c.property_address}</td>
                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold ring-1 ring-slate-200 whitespace-nowrap">{c.appraisal_status}</span></td>
                    <td className="px-3 py-2 hidden lg:table-cell text-slate-500">{c.work_booked_date ? new Date(c.work_booked_date).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }) : '—'}</td>
                    <td className="px-3 py-2 hidden lg:table-cell text-slate-500 truncate max-w-[140px]">{c.contractor_type || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/appraisals/${c._id}`} className="text-[10px] text-slate-400 hover:text-[#D35400] inline-flex items-center gap-1">Open <ChevronRight size={10} /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Stacks */}
      <div data-testid="stack-history" className="bg-white border border-[#E2E2DF] rounded-md">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <History size={14} className="text-[#D35400]" /> Recent Stacks
          </h2>
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{history.length} run{history.length === 1 ? '' : 's'}</span>
        </header>
        {history.length === 0 ? (
          <p className="text-center py-4 text-[11px] text-slate-400 italic">No stacks generated yet — your runs will be listed here.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map(h => (
              <li key={h._id} data-testid={`stack-history-row-${h._id}`} className="flex items-center justify-between gap-2 px-4 py-2 text-[11px] hover:bg-amber-50/30">
                <div>
                  <p className="font-bold text-slate-800">{h.doc_type_label} — {h.appraisal_count} job{h.appraisal_count === 1 ? '' : 's'}</p>
                  <p className="text-[10px] text-slate-500">
                    {new Date(h.generated_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {h.generated_by}
                    {h.failed_count > 0 && <span className="text-red-600"> · {h.failed_count} failure{h.failed_count === 1 ? '' : 's'}</span>}
                  </p>
                </div>
                <span className="text-[10px] font-mono-num text-slate-400">{h.successful} ✓</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
